import { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Plus, Trash2, X, Users, ChevronDown, ChevronRight } from "lucide-react";

const inputCls = "mt-1 h-9 bg-slate-900/60 border-slate-700";

// Campi di un singolo collaboratore (riusati in editor multiplo e dialog di inserimento)
export function CollaboratoreFields({ value, onChange, idPrefix = "collab" }) {
  const c = value || {};
  const set = (k, v) => onChange({ ...c, [k]: v });
  const arr = (k) => (Array.isArray(c[k]) ? c[k] : []);
  const setItem = (k, i, v) => { const a = [...arr(k)]; a[i] = v; set(k, a); };
  const add = (k) => set(k, [...arr(k), ""]);
  const del = (k, i) => {
    const a = [...arr(k)]; a.splice(i, 1);
    if (k === "cellulare") { const l = [...arr("cellulare_etichette")]; l.splice(i, 1); onChange({ ...c, cellulare: a, cellulare_etichette: l }); return; }
    set(k, a);
  };
  const setLabel = (i, v) => { const l = Array.from({ length: Math.max(arr("cellulare_etichette").length, i + 1) }, (_, j) => arr("cellulare_etichette")[j] || ""); l[i] = v.slice(0, 30); set("cellulare_etichette", l); };

  const multi = (k, label, ph) => (
    <div className="sm:col-span-2">
      <Label className="text-slate-300 text-xs">{label}</Label>
      <div className="space-y-1.5 mt-1">
        {arr(k).map((v, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input value={v} onChange={(e) => setItem(k, i, e.target.value)} placeholder={ph} data-testid={`${idPrefix}-${k}-${i}`} className={`h-9 bg-slate-900/60 border-slate-700 ${k === "cellulare" ? "!w-[40%] shrink-0" : ""}`} />
            {k === "cellulare" && <Input value={arr("cellulare_etichette")[i] || ""} onChange={(e) => setLabel(i, e.target.value)} placeholder="Di chi?" title="Etichetta del numero (opzionale)" data-testid={`${idPrefix}-cellulare-label-${i}`} className="h-9 flex-1 min-w-0 bg-slate-900/60 border-slate-700" />}
            <button type="button" onClick={() => del(k, i)} data-testid={`${idPrefix}-${k}-del-${i}`} title="Rimuovi" className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-slate-700 text-red-400 hover:bg-red-500/10"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" onClick={() => add(k)} data-testid={`${idPrefix}-${k}-add`} className="text-xs font-medium text-teal-300 hover:text-teal-200 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Aggiungi {label.toLowerCase()}</button>
      </div>
    </div>
  );

  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div><Label className="text-slate-300 text-xs">Cognome</Label><Input value={c.cognome || ""} onChange={(e) => set("cognome", e.target.value)} data-testid={`${idPrefix}-cognome`} className={inputCls} /></div>
      <div><Label className="text-slate-300 text-xs">Nome</Label><Input value={c.nome || ""} onChange={(e) => set("nome", e.target.value)} data-testid={`${idPrefix}-nome`} className={inputCls} /></div>
      <div><Label className="text-slate-300 text-xs">Codice Fiscale</Label><Input value={c.codice_fiscale || ""} onChange={(e) => set("codice_fiscale", e.target.value)} data-testid={`${idPrefix}-cf`} className={inputCls} /></div>
      <div><Label className="text-slate-300 text-xs">Data di Nascita</Label><Input type="date" value={c.data_nascita || ""} onChange={(e) => set("data_nascita", e.target.value)} data-testid={`${idPrefix}-data_nascita`} className={`${inputCls} [color-scheme:dark]`} /></div>
      <div className="sm:col-span-2"><Label className="text-slate-300 text-xs">Indirizzo</Label><Input value={c.indirizzo || ""} onChange={(e) => set("indirizzo", e.target.value)} data-testid={`${idPrefix}-indirizzo`} className={inputCls} /></div>
      {multi("email", "Email", "email@esempio.it")}
      {multi("cellulare", "Cellulare", "es. 333 1234567")}
      {multi("telefono", "Telefono", "es. 02 1234567")}
      {multi("pec", "PEC", "nome@pec.it")}
    </div>
  );
}

// Sottosezione collassabile con l'elenco dei collaboratori di un professionista.
export default function CollaboratoriEditor({ value = [], onChange, testId = "collaboratori" }) {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(value) ? value : [];
  const setItem = (i, v) => { const a = [...list]; a[i] = v; onChange(a); };
  const add = () => { onChange([...list, { nome: "", cognome: "", codice_fiscale: "", data_nascita: "", indirizzo: "", email: [], cellulare: [] }]); setOpen(true); };
  const del = (i) => { const a = [...list]; a.splice(i, 1); onChange(a); };

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`} className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-800/60 hover:bg-slate-800 text-left transition-colors">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-200 flex items-center gap-2"><Users className="h-4 w-4 text-amber-400" /> Collaboratori {list.length > 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{list.length}</span>}</span>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="p-4 bg-slate-900/20 space-y-4">
          {list.length === 0 && <p className="text-xs text-slate-500">Nessun collaboratore censito. Aggiungine uno con il pulsante qui sotto.</p>}
          {list.map((c, i) => (
            <div key={i} className="border border-slate-800 rounded-lg p-3 bg-slate-900/40" data-testid={`${testId}-item-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-amber-300">Collaboratore #{i + 1}</span>
                <button type="button" onClick={() => del(i)} data-testid={`${testId}-del-${i}`} title="Elimina collaboratore" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
              </div>
              <CollaboratoreFields value={c} onChange={(v) => setItem(i, v)} idPrefix={`${testId}-${i}`} />
              <div className="mt-3">
                <label className="text-slate-300 text-xs font-medium">Note</label>
                <textarea value={c.note || ""} rows={3} placeholder="Annotazioni libere…" data-testid={`${testId}-${i}-note`}
                  onChange={(e) => { setItem(i, { ...c, note: e.target.value }); e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }}
                  className="mt-1 w-full min-h-[80px] resize-y rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500/60" />
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={add} data-testid={`${testId}-add`} className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10"><Plus className="h-4 w-4 mr-1" /> Aggiungi collaboratore</Button>
        </div>
      )}
    </div>
  );
}
